// The screens either side of the interview: the shell, the welcome, and the
// sign-off.
//
// WHY THEY LIVE TOGETHER
// They are one continuous experience for the candidate — arrive, prepare, sit
// the interview, leave — and when they lived as ad-hoc JSX inside AiInterview
// they drifted into three different visual languages, two of them light-themed
// against a dark interview. Crossing from the welcome into the interview looked
// like crossing between two products.
//
// EVERYTHING IS DARK, INCLUDING THE PARTS BEFORE THE CAMERA OPENS. The candidate
// should not experience a lighting change halfway through.

import React, { useEffect, useRef } from "react";
import { Box, Button, Typography, Stack, CircularProgress } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InterviewerOrb from "./InterviewerOrb";
import { FONT, MONO, dark as C, radius, motion, injectKeyframes } from "./theme";

injectKeyframes();

// ── Shared shell ────────────────────────────────────────────────────────────

export const Shell = ({ children, wide }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: C.bg,
      color: C.ink,
      fontFamily: FONT,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: { xs: 2.5, md: 4 },
      // A single warm bloom behind everything, so the page has a light source
      // rather than being uniformly dark. Flat black reads as unfinished.
      backgroundImage: `radial-gradient(120% 90% at 50% -10%, rgba(${C.mayaGlow},.09) 0%, transparent 60%)`,
    }}
  >
    <Box sx={{ width: "100%", maxWidth: wide ? 980 : 620, animation: `hRise .45s ${motion.base.split(" ")[1]} both` }}>
      {children}
    </Box>
  </Box>
);

export const Loading = () => (
  <Shell>
    <Stack alignItems="center" spacing={2.5} sx={{ py: 6 }}>
      <CircularProgress size={26} sx={{ color: C.maya }} />
      <Typography sx={{ fontSize: 13.5, color: C.muted }}>
        Opening your interview…
      </Typography>
    </Stack>
  </Shell>
);

export const Failure = ({ message, code }) => {
  // The extra line is the whole value of this screen. "This link is not valid"
  // leaves a candidate stuck; telling them a newer email exists lets them fix it
  // themselves rather than emailing to ask.
  const help = {
    TOO_EARLY: "Come back at your scheduled time using this same link.",
    LINK_SUPERSEDED: "A newer invitation was sent to you — please open the most recent email.",
    NO_ATTEMPTS_LEFT: "If you think this is wrong, reply to the invitation email and someone will look into it.",
    WINDOW_CLOSED: "Reply to your invitation email and the team can reopen it for you.",
  }[code];

  const done = code === "SUBMITTED" || code === "SCORED";

  return (
    <Shell>
      <Stack alignItems="center" spacing={2.5} sx={{ textAlign: "center", py: 4 }}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: "50%",
            display: "grid", placeItems: "center",
            bgcolor: done ? C.actSoft : "rgba(251,113,133,.12)",
            color: done ? C.act : C.negative,
          }}
        >
          {done ? <CheckRoundedIcon /> : <ErrorOutlineIcon />}
        </Box>
        <Typography sx={{ fontSize: 19, fontWeight: 650, maxWidth: "34ch" }}>
          {message}
        </Typography>
        {help && (
          <Typography sx={{ fontSize: 14, color: C.muted, maxWidth: "42ch", lineHeight: 1.7 }}>
            {help}
          </Typography>
        )}
      </Stack>
    </Shell>
  );
};

// ── Welcome ─────────────────────────────────────────────────────────────────

const FACTS = [
  { icon: <ScheduleOutlinedIcon sx={{ fontSize: 19 }} />, label: "How long", value: (s) => `About ${s?.duration_minutes ?? 30} minutes, ${s?.total_questions ?? 12} questions` },
  { icon: <VideocamOutlinedIcon sx={{ fontSize: 19 }} />, label: "Recorded", value: () => "Video and sound, reviewed by a person on the team" },
  { icon: <MicNoneOutlinedIcon sx={{ fontSize: 19 }} />, label: "You'll need", value: () => "A working camera and microphone, and somewhere quiet" },
  { icon: <LockOutlinedIcon sx={{ fontSize: 19 }} />, label: "One way through", value: () => "You can't return to a question — but a dropped connection resumes" },
];

export const Welcome = ({ session, starting, onContinue }) => (
  <Shell>
    <Typography sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".13em", textTransform: "uppercase", color: C.maya, mb: 1.5 }}>
      First-round interview
    </Typography>

    <Typography sx={{ fontSize: { xs: 27, md: 34 }, fontWeight: 700, letterSpacing: "-.022em", lineHeight: 1.22 }}>
      {session?.job_title || "Interview"}
    </Typography>

    <Typography sx={{ fontSize: 15.5, color: C.muted, mt: 1.25, lineHeight: 1.7 }}>
      Hello {session?.candidate_name || "there"} — here's what to expect before you start.
    </Typography>

    {/* A definition list, not a numbered checklist. These are four facts about
        the interview, not four steps to perform, and numbering them implies an
        order that does not exist. */}
    <Stack spacing={0} sx={{ mt: 4, border: `1px solid ${C.hair}`, borderRadius: radius.md, overflow: "hidden" }}>
      {FACTS.map((f, i) => (
        <Stack
          key={f.label}
          direction="row"
          spacing={2}
          sx={{
            px: 2.5, py: 2,
            alignItems: "flex-start",
            borderTop: i ? `1px solid ${C.hair}` : "none",
            bgcolor: C.panel,
          }}
        >
          <Box sx={{ color: C.maya, mt: "1px" }}>{f.icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".1em", textTransform: "uppercase", color: C.faint, mb: 0.4 }}>
              {f.label}
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: C.body, lineHeight: 1.6 }}>
              {f.value(session)}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>

    <Button
      fullWidth
      disabled={starting}
      onClick={onContinue}
      endIcon={<ArrowForwardIcon />}
      sx={{
        mt: 3.5, py: 1.5, borderRadius: radius.md,
        fontFamily: FONT, textTransform: "none", fontSize: 16, fontWeight: 700,
        bgcolor: C.act, color: "#04231f",
        "&:hover": { bgcolor: "#5eead4" },
        "&.Mui-disabled": { bgcolor: C.line, color: C.faint },
      }}
    >
      {starting ? "Preparing…" : "Check my camera and continue"}
    </Button>

    <Typography sx={{ mt: 2, fontSize: 12.5, color: C.faint, textAlign: "center", lineHeight: 1.7 }}>
      Your camera and microphone are used only for this interview.
      {session?.attempts_used > 0 && ` · Attempt ${session.attempts_used + 1} of ${session.max_attempts}`}
    </Typography>
  </Shell>
);

// ── Sign-off ────────────────────────────────────────────────────────────────

export const Complete = ({ interviewer, speech }) => {
  const spokenRef = useRef(false);

  useEffect(() => {
    if (spokenRef.current) return;
    if (!interviewer?.spoken_farewell) return;
    spokenRef.current = true;
    // Index -1 is the sign-off on the voice endpoint.
    speech?.speak(interviewer.spoken_farewell, -1);
  }, [interviewer, speech]);

  const name = interviewer?.name || "Maya";
  const paragraphs = String(interviewer?.farewell || "")
    .split("\n")
    .filter((l) => l.trim());

  return (
    <Shell wide>
      <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 3, md: 5 }} alignItems="center">
        <Box sx={{ width: { xs: 168, md: 236 }, height: { xs: 168, md: 236 }, flexShrink: 0, borderRadius: radius.lg, overflow: "hidden" }}>
          <InterviewerOrb
            state={speech?.speaking ? "speaking" : speech?.loading ? "thinking" : "asking"}
            label={name}
          />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 26, height: 26, borderRadius: "50%",
                display: "grid", placeItems: "center",
                bgcolor: C.actSoft, color: C.act,
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".13em", textTransform: "uppercase", color: C.act }}>
              Interview complete
            </Typography>
          </Stack>

          {paragraphs.length ? (
            paragraphs.map((line, i) => (
              <Typography
                key={i}
                sx={{
                  // The first line is the one that matters emotionally, so it
                  // gets the weight. The rest is reassurance and process.
                  fontSize: i === 0 ? { xs: 22, md: 26 } : { xs: 14.5, md: 15.5 },
                  fontWeight: i === 0 ? 650 : 400,
                  letterSpacing: i === 0 ? "-.018em" : 0,
                  lineHeight: i === 0 ? 1.3 : 1.75,
                  color: i === 0 ? C.ink : C.body,
                  mb: i === 0 ? 2.5 : 1.75,
                  maxWidth: "56ch",
                }}
              >
                {line}
              </Typography>
            ))
          ) : (
            <Typography sx={{ fontSize: 22, fontWeight: 650 }}>
              That's everything — thank you.
            </Typography>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: 12.5, color: C.faint }}>
              You can close this window.
            </Typography>
            {speech?.supported && (
              <Button
                size="small"
                onClick={() => speech.setEnabled(!speech.enabled)}
                startIcon={speech.enabled ? <VolumeUpIcon sx={{ fontSize: 17 }} /> : <VolumeOffIcon sx={{ fontSize: 17 }} />}
                sx={{
                  fontFamily: FONT, textTransform: "none", fontWeight: 600, fontSize: 12.5,
                  color: speech.enabled ? C.maya : C.faint, minWidth: 0,
                }}
              >
                {speech.enabled ? "Voice on" : "Voice off"}
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </Shell>
  );
};

export default { Shell, Loading, Failure, Welcome, Complete };
